import React from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import groupBy from 'lodash/groupBy';
import { useHistory } from 'react-router-dom';

import OrderItem from 'onramp/ui/components/OrderItem';
import { Box, Button, Flex, Text, Line } from 'core/ui/atoms';
import Loading from 'core/ui/components/Loading';
import { editWeekMenuPath } from 'onramp/api/navigation';

import { isLoading } from 'core/api/loadingHelpers';
import {
  GET_PRODUCT_COMPONENTS_BY_DELIVERY_DAY,
  GET_SWAPPED_PRODUCT_COMPONENTS_BY_DELIVERY_DAY,
  GET_PROGRAM_DETAIL,
} from 'core/api/apollo/queries';

const OrderList = ({ deliveryPeriod }) => {
  const history = useHistory();
  const variables = { deliveryDayId: deliveryPeriod?.id };

  const { loading, error, data: {
    getProductComponentsByDeliveryDay = [] } = {},
  } = useQuery(GET_PRODUCT_COMPONENTS_BY_DELIVERY_DAY, {
    variables,
  });

  const {
    data: { getSwappedProductComponentsByDeliveryDay = [] } = {},
  } = useQuery(GET_SWAPPED_PRODUCT_COMPONENTS_BY_DELIVERY_DAY, {
    variables,
    skip: loading,
  });

  const { data: { getProgramDetail = {} } = {} } = useQuery(GET_PROGRAM_DETAIL);

  const onEditWeekMenu = () => {
    history.push(editWeekMenuPath(deliveryPeriod?.id));
  };

  if (isLoading(loading, getProductComponentsByDeliveryDay, error)) {
    return <Loading />;
  }

  const groupProductByDay = groupBy(getProductComponentsByDeliveryDay, 'day');

  const dayList = Object.keys(groupProductByDay).filter((data) => data !== 'null');

  const isCancelled = deliveryPeriod?.programCutoffPassed || getProgramDetail?.subscriptionActive !== null && !getProgramDetail?.subscriptionActive;

  const renderOrderItem = (orderItems) => {
    return orderItems.map((item, index) => {
      const swappedItem = getSwappedProductComponentsByDeliveryDay.find((swapped) => swapped.originalComponentId === item.id);
      if (swappedItem) {
        const swappedItemWithFlag = { ...swappedItem, swapped: true, editable: false };
        return <OrderItem item={swappedItemWithFlag} key={index} deliveryDayId={deliveryPeriod?.id} />;
      }
      return <OrderItem item={item} key={index} deliveryDayId={deliveryPeriod?.id} />;
    });
  };

  const RenderItem = () => {
    return dayList.map((dayItem, index) => (
      <Box key={index}>
        <Flex direction='row' alignItems='center' justifyContent='center' gap='16px'>
          <Line />
          <DayText>{`Day ${dayItem}`}</DayText>
          <Line />
        </Flex>
        <ScrollableContent>
          {renderOrderItem(groupProductByDay[dayItem])}
        </ScrollableContent>
      </Box>
    ));
  };

  return (
    <Container>
      <RenderItem />
      <EditWeekOrderBottom>
        <EditOrderButton disabled={isCancelled} onClick={onEditWeekMenu}>EDIT MY ORDER</EditOrderButton>
      </EditWeekOrderBottom>
    </Container>
  );
};

OrderList.propTypes = {
  deliveryPeriod: PropTypes.object.isRequired,
};

const EditWeekOrderBottom = styled(Flex)`
  display: flex;
  justify-content: center;
  position: fixed;
  width: 100%;
  bottom: 0;
  left: 0;
  padding: 20px;
  background-color: ${(p) => p.theme.colors.creamWhite};
  height: 80px;
`;

const EditOrderButton = styled(Button)`
  padding: 12px 10px;
  border-radius: 4px;
  display: flex;
  width: 85%;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background: ${(p) => p.theme.colors.green.light};
  ${(p) => p.theme.max('sm')`
    margin-bottom: 0;
  `}
`;

const DayText = styled(Text)`
  text-align: center;
  color: ${(p) => p.theme.colors.primaryV2};
  text-align: center;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  margin-bottom: 20px;
  margin-top: 20px;
`;

const ScrollableContent = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  width: 100%;
  gap: 24px;
  padding-bottom: 10px; /* Space for scrollbar */
  scrollbar-width: none;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  overflow-y: auto;
  column-gap: 25px;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    height: 6px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  /* For Firefox */
  scrollbar-color: #888 #f1f1f1;
`;

const Container = styled.div`
  margin-top: 15px;
  width: 95%;
  overflow: hidden;
`;

export default OrderList;
